/**
Service for fetching a profile for a given geo model layer and a series of coordinates.
*/

import { Borehole } from './Borehole'
import { GeoModel } from './GeoModel'
import { GeoWebServiceClient } from './GeoWebServiceClient'

/**
 * Object type describing the returned profile.
 */
export interface ProfileObject {
  Bathymetrics: any
  Borehole: Borehole
  BoreholeMaxDistance: number
  BoreholeNearestPoint: any
  HydroModels: any
  Model: GeoModel
  Path: []
  PathLength: number
  Svg: string
  SvgLayout: {}
  Tag: string
  ZMax: number
  ZMin: number
}

/**
 * Web service for fetching a profile for a given geo model layer and a series of coordinates.
 * The following parameters are required: 'ModelId' ( @see setModelId ) and 'Path' ( @see setPath ).
 */
export class CrossSection extends GeoWebServiceClient {
  static get URL (): string { return 'https://data.geo.dk/api/v3/crosssection' }

  /**
   * Constructor.
   * @param token The Geo auth token.
   */
  constructor (token: string) {
    const defaultParams = {
      srid: '25832'
    }
    super(CrossSection.URL, token, defaultParams)
  }

  /**
   * Setter for hydro model ids.
   * @param hydroModelIds Comma separated list of hydro model ids.
   */
  setHydroModelIds (hydroModelIds: string): void {
    this.addParam('hydromodelid', hydroModelIds)
  }

  /**
   * Setter for max borehole distance.
   * @param maxBoreholeDistance The max borehole distance.
   */
  setMaxBoreholeDistance (maxBoreholeDistance: number): void {
    this.addParam('maxBoringDistance', maxBoreholeDistance.toString())
  }

  /**
   * Setter for max depth.
   * @param maxDepth The max depth.
   */
  setMaxDepth (maxDepth: number): void {
    this.addParam('maxDepth', maxDepth.toString())
  }

  /**
   * [Required*] Setter for the model id.
   * @param modelId The model id.
   */
  setModelId (modelId: string): void {
    this.addParam('geoModelId', modelId)
  }

  /**
   * [Required*] Setter for the path coordinates.
   * @param path The path coordinates.
   */
  setPathCoordinates (path: ol.Coordinate[]): void {
    let coordinates = ''
    path.forEach((coordinate: ol.Coordinate) => {
      coordinates = `${coordinates !== '' ? coordinates + ',' : ''}[${coordinate[0]},${coordinate[1]}]`
    })
    this.addParam('path', '[' + coordinates + ']')
  }

  /**
   * Setter for width in pixels.
   * @param width The width of the figure in pixels.
   */
  setWidth (width: number): void {
    this.addParam('width', width.toString())
  }

  /**
   * Setter for height in pixels.
   * @param height The height of the figure in pixels.
   */
  setHeight (height: number): void {
    this.addParam('height', height.toString())
  }

  /**
   * Validates that required params are given.
   */
  private validateParams (): void {
    if (!(this.hasParam('geoModelId')) || !(this.hasParam('path'))) {
      throw new Error('Parameter mismatch: The modelId and path params must be set.')
    }
  }

  /**
   * Calls the web service.
   * @returns A promise bearing the profile object.
   */
  async fetchProfile (): Promise<ProfileObject> {
    this.validateParams()
    const response: Response = await fetch(this.url(), { headers: { Authorization: `Bearer ${this.token}` } })
    if (!response.ok) {
      throw new Error('Got invalid HTTP response.')
    }
    return await response.json()
  }
}
